// Turns a raw MediaPipe FaceLandmarker result for one face into a FaceObservation:
// head orientation, gaze centering, eye openness, a combined "looking at me"
// lock score, and an approximate distance.

import { CONFIG } from "../config";
import type { BBox, FaceAction, FaceObservation } from "../types";

interface Pt {
  x: number;
  y: number;
  z: number;
}
interface Category {
  categoryName?: string;
  displayName?: string;
  score: number;
}

const RAD2DEG = 180 / Math.PI;

/** MediaPipe iris centre landmarks (present when the model outputs 478 points). */
const LEFT_IRIS = 468;
const RIGHT_IRIS = 473;

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/**
 * Head yaw/pitch/roll (degrees) from the 4x4 facial transformation matrix.
 * DISPLAY ONLY — the lock decision does not depend on this, because the matrix's
 * axis-sign convention varies across MediaPipe versions. "Facing" is derived
 * from landmark geometry instead (see `headFacingFromLandmarks`).
 */
function extractPose(m: ArrayLike<number>): { yaw: number; pitch: number; roll: number } {
  const r22 = m[10] ?? 1;
  const yaw = Math.atan2(m[8] ?? 0, r22) * RAD2DEG;
  const pitch = -Math.atan2(m[9] ?? 0, r22) * RAD2DEG;
  const roll = Math.atan2(m[1] ?? 0, m[0] ?? 1) * RAD2DEG;
  return { yaw, pitch, roll };
}

function lookup(cats: Category[], name: string): number {
  for (const c of cats) {
    if (c.categoryName === name || c.displayName === name) return c.score;
  }
  return 0;
}

/**
 * Horizontal + vertical gaze magnitudes (0..1 each) and the combined magnitude,
 * from the eye-look blendshapes. Horizontal uses in/out looks, vertical uses
 * up/down looks, averaged across the two eyes. Larger = looking further away
 * from the camera axis.
 */
function gazeComponents(cats: Category[]): { h: number; v: number; mag: number } {
  const hL = Math.max(lookup(cats, "eyeLookInLeft"), lookup(cats, "eyeLookOutLeft"));
  const vL = Math.max(lookup(cats, "eyeLookUpLeft"), lookup(cats, "eyeLookDownLeft"));
  const hR = Math.max(lookup(cats, "eyeLookInRight"), lookup(cats, "eyeLookOutRight"));
  const vR = Math.max(lookup(cats, "eyeLookUpRight"), lookup(cats, "eyeLookDownRight"));
  const h = (hL + hR) / 2;
  const v = (vL + vR) / 2;
  const mag = (Math.hypot(hL, vL) + Math.hypot(hR, vR)) / 2;
  return { h, v, mag };
}

/** 0..1 — how centered the gaze is (0 = looking away). */
function gazeCentering(mag: number): number {
  return clamp01(1 - mag / CONFIG.lockOn.gazeZeroMag);
}

/** MediaPipe FaceMesh landmark indices that ring the two eyes (for the eye tile). */
const EYE_INDICES = [
  33, 133, 159, 145, 158, 153, 160, 144, // left eye lids + corners
  362, 263, 386, 374, 385, 380, 387, 373, // right eye lids + corners
  LEFT_IRIS, RIGHT_IRIS,
];

/** Tight normalized box spanning both eyes, for the magnified "zoom into eyes" tile. */
function eyesBBoxFromLandmarks(landmarks: Pt[]): BBox {
  let minX = 1;
  let minY = 1;
  let maxX = 0;
  let maxY = 0;
  for (const idx of EYE_INDICES) {
    const p = landmarks[idx];
    if (!p) continue;
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  if (maxX <= minX || maxY <= minY) return faceBBox(landmarks);
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

/**
 * Eye-aspect-ratio (vertical lid opening / horizontal eye width) for one eye,
 * or -1 if its landmarks are missing. Geometric, so it is a harder blink signal
 * than the soft eyeBlink blendshape.
 */
function earOne(lm: Pt[], outer: number, inner: number, upper: number, lower: number): number {
  const o = lm[outer];
  const i = lm[inner];
  const u = lm[upper];
  const d = lm[lower];
  if (!o || !i || !u || !d) return -1;
  const w = Math.hypot(o.x - i.x, o.y - i.y) || 1e-5;
  return Math.hypot(u.x - d.x, u.y - d.y) / w;
}

/** 0..1 — eyes open (1) vs blinking/closed (0), from the eye-aspect-ratio. */
function eyeOpenness(cats: Category[], landmarks: Pt[]): number {
  const l = earOne(landmarks, 33, 133, 159, 145);
  const r = earOne(landmarks, 362, 263, 386, 374);
  const vals = [l, r].filter((v) => v >= 0);
  if (vals.length > 0) {
    const ear = vals.reduce((a, b) => a + b, 0) / vals.length;
    const { earOpen, earClosed } = CONFIG.lockOn;
    return clamp01((ear - earClosed) / (earOpen - earClosed));
  }
  // Fallback to the blink blendshape if lid landmarks are unavailable.
  const blink = Math.max(lookup(cats, "eyeBlinkLeft"), lookup(cats, "eyeBlinkRight"));
  return clamp01(1 - blink);
}

// Expressive blendshapes we surface as facial-cue pills. The gaze/blink action
// units are excluded (they are used for the lock score, not read as "tells").
const EXPRESSIVE = [
  "browInnerUp", "browOuterUpLeft", "browOuterUpRight", "browDownLeft", "browDownRight",
  "cheekSquintLeft", "cheekSquintRight", "cheekPuff",
  "noseSneerLeft", "noseSneerRight",
  "mouthSmileLeft", "mouthSmileRight", "mouthFrownLeft", "mouthFrownRight",
  "mouthPressLeft", "mouthPressRight", "mouthDimpleLeft", "mouthDimpleRight",
  "mouthShrugUpper", "mouthShrugLower", "mouthPucker", "mouthFunnel",
  "mouthStretchLeft", "mouthStretchRight", "mouthUpperUpLeft", "mouthUpperUpRight",
  "mouthLowerDownLeft", "mouthLowerDownRight", "mouthRollLower", "mouthRollUpper",
  "jawOpen", "jawForward", "jawLeft", "jawRight",
  "eyeSquintLeft", "eyeSquintRight", "eyeWideLeft", "eyeWideRight",
  "mouthClose", "mouthLeft", "mouthRight", "tongueOut",
];

/** Strip the Left/Right suffix so paired action units merge into one pill. */
function baseName(n: string): string {
  return n.replace(/(Left|Right)$/, "");
}

/**
 * Top active facial muscle movements this frame, merged Left/Right (max), above
 * a threshold, strongest first. These drive the 3-word facial-cue pills.
 */
function topActions(cats: Category[], threshold: number, limit: number): FaceAction[] {
  const merged = new Map<string, number>();
  for (const name of EXPRESSIVE) {
    const score = lookup(cats, name);
    if (score < threshold) continue;
    const key = baseName(name);
    const prev = merged.get(key) ?? 0;
    if (score > prev) merged.set(key, score);
  }
  return [...merged.entries()]
    .map(([name, score]) => ({ name, score }))
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);
}

function faceBBox(landmarks: Pt[]): BBox {
  let minX = 1;
  let minY = 1;
  let maxX = 0;
  let maxY = 0;
  for (const p of landmarks) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

/** Normalized iris diameter from its 4 ring landmarks (0 if any are missing). */
function irisDiameterNorm(lm: Pt[], a: number, b: number, c: number, d: number): number {
  const pa = lm[a];
  const pb = lm[b];
  const pc = lm[c];
  const pd = lm[d];
  if (!pa || !pb || !pc || !pd) return 0;
  const horiz = Math.hypot(pa.x - pc.x, pa.y - pc.y);
  const vert = Math.hypot(pb.x - pd.x, pb.y - pd.y);
  return (horiz + vert) / 2;
}

/**
 * Approximate distance, preferring the iris DIAMETER (very constant at ~11.7mm,
 * so ~4% error) over the inter-pupillary distance (~20% variance) which is used
 * only as a fallback. The frame width cancels, so this depends only on the
 * assumed real size and camera FOV. Uncalibrated, a hint not a measurement.
 */
function estimateDistance(landmarks: Pt[]): number {
  const halfFov = (CONFIG.distance.assumedHFovDeg * Math.PI) / 180 / 2;

  // Iris ring landmarks: left 469-472 (centre 468), right 474-477 (centre 473).
  const dL = irisDiameterNorm(landmarks, 469, 470, 471, 472);
  const dR = irisDiameterNorm(landmarks, 474, 475, 476, 477);
  const diam = dL > 0 && dR > 0 ? (dL + dR) / 2 : dL || dR;
  if (diam > 1e-5) {
    return (CONFIG.distance.realIrisMeters * 0.5) / (Math.tan(halfFov) * diam);
  }

  // Fallback: inter-pupillary distance from the two iris centres.
  const li = landmarks[LEFT_IRIS];
  const ri = landmarks[RIGHT_IRIS];
  if (!li || !ri) return 0;
  const ipdNorm = Math.hypot(li.x - ri.x, li.y - ri.y);
  if (ipdNorm <= 1e-5) return 0;
  return (CONFIG.distance.realIpdMeters * 0.5) / (Math.tan(halfFov) * ipdNorm);
}

// Model-based eye-in-head gaze: where the iris sits inside the eye opening,
// relative to the eye corners (horizontal) and lids (vertical). This is largely
// head-pose invariant (both the iris and the corners move together when the head
// turns), so it is a cleaner "where are the eyes pointing" estimate than the
// eye-look blendshape magnitude. Deep-research flagged model-based gaze as more
// accurate than appearance blendshapes for this. Still approximate, uncalibrated.
interface EyeRatio {
  h: number; // horizontal iris offset, ~ -0.5 (in) .. +0.5 (out) of eye width
  v: number; // vertical iris offset, ~ -0.5 (up) .. +0.5 (down) of eye height
}

function eyeRatio(lm: Pt[], outer: number, inner: number, iris: number, upper: number, lower: number): EyeRatio | null {
  const o = lm[outer];
  const i = lm[inner];
  const c = lm[iris];
  const u = lm[upper];
  const d = lm[lower];
  if (!o || !i || !c || !u || !d) return null;
  const cxMid = (o.x + i.x) / 2;
  const cyMid = (u.y + d.y) / 2;
  const w = Math.hypot(o.x - i.x, o.y - i.y) || 1e-5;
  const hgt = Math.hypot(u.x - d.x, u.y - d.y) || 1e-5;
  return { h: (c.x - cxMid) / w, v: (c.y - cyMid) / hgt };
}

// An iris offset of ~half the eye width corresponds to a large eye rotation. We
// treat a full 0.5 ratio as roughly 32 degrees of gaze, clamped.
const YAW_PER_RATIO = (32 * Math.PI) / 180 / 0.5;
const PITCH_PER_RATIO = (26 * Math.PI) / 180 / 0.5;

/** Iris-based off-axis gaze angles (radians), or null if iris landmarks absent. */
function irisGaze(lm: Pt[]): { yaw: number; pitch: number } | null {
  const left = eyeRatio(lm, 33, 133, LEFT_IRIS, 159, 145);
  const right = eyeRatio(lm, 362, 263, RIGHT_IRIS, 386, 374);
  if (!left || !right) return null;
  const h = (left.h + right.h) / 2;
  const v = (left.v + right.v) / 2;
  return { yaw: h * YAW_PER_RATIO, pitch: v * PITCH_PER_RATIO };
}

/** Build the per-frame observation for one detected face. */
export function buildObservation(
  landmarks: Pt[],
  blendCategories: Category[],
  matrixData: ArrayLike<number> | undefined,
): FaceObservation {
  const bbox = faceBBox(landmarks);
  const pose = matrixData ? extractPose(matrixData) : { yaw: 0, pitch: 0, roll: 0 };

  const headFacing = headFacingFromLandmarks(landmarks);
  const gaze = gazeComponents(blendCategories);
  const gazeCentered = gazeCentering(gaze.mag);
  const eyeOpen = eyeOpenness(blendCategories, landmarks);
  // Eyes only disqualify a lock when clearly CLOSED (a real blink). MediaPipe's
  // blink blendshape is nonzero even for wide-open eyes, so give full credit
  // above ~0.6 open and only penalise as they close.
  const eyeFactor = clamp01((eyeOpen - 0.2) / 0.4);
  // Head orientation dominates ("are they facing me?"); gaze modulates it but
  // keeps a floor so a brief glance doesn't unlock a clearly frontal face.
  const gazeTerm = 0.3 + 0.7 * gazeCentered;
  const lockScore = clamp01(headFacing * gazeTerm * eyeFactor);

  // Prefer model-based iris gaze for the off-axis angles (more accurate); fall
  // back to the eye-look blendshape magnitudes if iris landmarks are missing.
  const RAD_PER_MAG = (25 * Math.PI) / 180 / CONFIG.lockOn.gazeZeroMag;
  const iris = irisGaze(landmarks);
  const gazeYawRad = iris ? iris.yaw : gaze.h * RAD_PER_MAG;
  const gazePitchRad = iris ? iris.pitch : gaze.v * RAD_PER_MAG;

  return {
    bbox,
    cx: bbox.x + bbox.w / 2,
    cy: bbox.y + bbox.h / 2,
    yaw: pose.yaw,
    pitch: pose.pitch,
    roll: pose.roll,
    headFacing,
    gazeCentered,
    eyeOpen,
    lockScore,
    distanceM: estimateDistance(landmarks),
    eyesBBox: eyesBBoxFromLandmarks(landmarks),
    gazeYawRad,
    gazePitchRad,
    actions: topActions(blendCategories, 0.34, 3),
  };
}

/**
 * Sign-free head-facing estimate from landmark geometry: how symmetric the nose
 * sits between the two outer eye corners. Frontal → ~1, turning away → ~0.
 * Robust across MediaPipe versions (no matrix-sign dependency).
 */
function headFacingFromLandmarks(landmarks: Pt[]): number {
  const nose = landmarks[1];
  const eyeA = landmarks[33]; // one outer eye corner
  const eyeB = landmarks[263]; // the other outer eye corner
  if (!nose || !eyeA || !eyeB) return 0.5;
  const dA = Math.abs(nose.x - eyeA.x);
  const dB = Math.abs(eyeB.x - nose.x);
  const sum = dA + dB;
  if (sum <= 1e-5) return 0.5;
  const asymmetry = Math.abs(dA - dB) / sum; // 0 frontal, →1 in profile
  return clamp01(1 - asymmetry * 1.7);
}
